/**
 * Schedule Generator - Generates multiple schedule candidates and selects the best one
 */
import Scheduler from './scheduler';

/**
 * Calculate diversity statistics for a schedule
 * @param {Object} schedule - Generated schedule
 * @param {Array} players - Array of players
 * @returns {Object} Statistics object
 */
export function calculateScheduleStats(schedule, players) {
  const n = players.length;

  // Initialize counters
  const partnerPairs = {};
  const opponentPairs = {};

  // Count partnerships and opponent encounters
  schedule.rounds.forEach(round => {
    round.matches.forEach(match => {
      const { team1, team2 } = match;

      // Count partnerships
      const partner1 = `${players[team1[0]].name} & ${players[team1[1]].name}`;
      const partner2 = `${players[team2[0]].name} & ${players[team2[1]].name}`;

      partnerPairs[partner1] = (partnerPairs[partner1] || 0) + 1;
      partnerPairs[partner2] = (partnerPairs[partner2] || 0) + 1;

      // Count opponent encounters
      team1.forEach(p1 => {
        team2.forEach(p2 => {
          const pair = `${players[p1].name} vs ${players[p2].name}`;
          opponentPairs[pair] = (opponentPairs[pair] || 0) + 1;
        });
      });
    });
  });

  // Calculate diversity scores
  const uniquePartnerPairs = Object.keys(partnerPairs).length;
  const totalPartnerRepetitions = Object.values(partnerPairs).reduce(
    (sum, count) => sum + Math.max(0, count - 1),
    0
  );

  const uniqueOpponentPairs = Object.keys(opponentPairs).length;
  const totalOpponentRepetitions = Object.values(opponentPairs).reduce(
    (sum, count) => sum + Math.max(0, count - 1),
    0
  );

  // Calculate maximum possible unique pairs
  const maxPossiblePartnerPairs = (n / 2) * (n / 2 - 1); // Combinations of left-right pairs
  const maxPossibleOpponentPairs = n * (n - 1); // Each player can face n-1 others

  // Diversity scores (0-1, higher is better)
  const partnerDiversityScore = uniquePartnerPairs / (uniquePartnerPairs + totalPartnerRepetitions);
  const opponentDiversityScore = uniqueOpponentPairs / (uniqueOpponentPairs + totalOpponentRepetitions);

  // Total matches count
  const totalMatches = schedule.rounds.reduce((sum, round) => sum + round.matches.length, 0);

  return {
    global: {
      partnerDiversityScore,
      opponentDiversityScore,
      uniquePartnerPairs,
      maxPossiblePartnerPairs,
      totalPartnerRepetitions,
      uniqueOpponentPairs,
      maxPossibleOpponentPairs,
      totalOpponentRepetitions,
      totalMatches,
      totalRounds: schedule.rounds.length,
      playerCount: n
    },
    partnerPairs,
    opponentPairs
  };
}

/**
 * Generate N schedule candidates and return the best one
 * @param {Array} players - Array of player objects {name, position, fixedPartnerId}
 * @param {number} courtCount - Number of courts
 * @param {number} roundCount - Number of rounds
 * @param {number} matchDuration - Match duration in minutes
 * @param {number} pauseDuration - Pause duration in minutes
 * @param {number} candidateCount - Number of candidates to generate (default 500)
 * @param {Function} onProgress - Progress callback (current, total, bestScore)
 * @returns {Promise<Object>} Best schedule with stats
 */
export async function generateBestSchedule(
  players,
  courtCount,
  roundCount,
  matchDuration,
  pauseDuration,
  candidateCount = 500,
  onProgress = null
) {
  // Add index-based IDs to players for scheduler compatibility
  const playersWithIds = players.map((player, index) => ({
    ...player,
    id: index
  }));

  let bestSchedule = null;
  let bestScore = -1;
  let bestStats = null;

  let totalScore = 0;
  let minScore = 1;

  for (let i = 0; i < candidateCount; i++) {
    // Generate schedule with unique seed for each candidate
    const seed = Date.now() + i;
    const schedule = Scheduler.generateSchedule(
      playersWithIds,
      courtCount,
      roundCount,
      matchDuration,
      pauseDuration,
      seed
    );

    // Calculate statistics for this schedule
    const stats = calculateScheduleStats(schedule, playersWithIds);

    // Calculate global score (average of partner and opponent diversity)
    const rawScore = (stats.global.partnerDiversityScore + stats.global.opponentDiversityScore) / 2;
    const globalScore = isNaN(rawScore) ? 0 : rawScore;

    totalScore += globalScore;
    if (globalScore < minScore) {
      minScore = globalScore;
    }

    // Keep track of best schedule
    if (globalScore > bestScore) {
      bestScore = globalScore;
      bestSchedule = schedule;
      bestStats = stats;
    }

    // Progress callback
    if (onProgress) {
      onProgress(i + 1, candidateCount, bestScore);
    }

    // Yield to event loop to prevent blocking UI
    if (i % 10 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  const avgScore = totalScore / candidateCount;

  return {
    schedule: bestSchedule,
    stats: bestStats,
    bestScore,
    avgScore,
    minScore,
    candidatesGenerated: candidateCount
  };
}

/**
 * Generate a single schedule (for testing or quick generation)
 * @param {Array} players - Array of player objects
 * @param {number} courtCount - Number of courts
 * @param {number} roundCount - Number of rounds
 * @param {number} matchDuration - Match duration in minutes
 * @param {number} pauseDuration - Pause duration in minutes
 * @param {number} seed - Optional seed for reproducibility
 * @returns {Object} Schedule with stats
 */
export function generateSingleSchedule(
  players,
  courtCount,
  roundCount,
  matchDuration,
  pauseDuration,
  seed = null
) {
  // Add index-based IDs to players
  const playersWithIds = players.map((player, index) => ({
    ...player,
    id: index
  }));

  const schedule = Scheduler.generateSchedule(
    playersWithIds,
    courtCount,
    roundCount,
    matchDuration,
    pauseDuration,
    seed
  );

  const stats = calculateScheduleStats(schedule, playersWithIds);

  return {
    schedule,
    stats
  };
}
